import { Component, OnInit, signal } from '@angular/core';
import {
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { UserOrg } from 'src/app/interfaces';
import {
  CleanOrphanedOrganisationUsersService,
  CleanupOutcome,
  CleanupResult,
} from './clean-orphaned-organisation-users.service';

interface RootUserPreview {
  uid: string;
  email: string | null;
  organisations: UserOrg[];
}

// Legacy user documents store `organisations` as an object map keyed by organisation ID.
export function normaliseUserOrganisations(value: unknown): UserOrg[] {
  if (Array.isArray(value)) {
    return value.filter(
      (organisation): organisation is UserOrg =>
        !!organisation && typeof organisation === 'object' && typeof organisation.id === 'string',
    );
  }

  if (!!value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).map(([id, organisation]) =>
      !!organisation && typeof organisation === 'object'
        ? { ...(organisation as UserOrg), id }
        : { id, name: id },
    );
  }

  return [];
}

export function getOrphanedOrganisationIds(
  users: RootUserPreview[],
  existingOrganisationIds: Set<string>,
): string[] {
  return [
    ...new Set(
      users
        .flatMap((user) => user.organisations.map((organisation) => organisation.id))
        .filter((organisationId) => !existingOrganisationIds.has(organisationId)),
    ),
  ].sort();
}

@Component({
  selector: 'app-clean-orphaned-organisation-users',
  templateUrl: './clean-orphaned-organisation-users.component.html',
  styleUrls: ['./clean-orphaned-organisation-users.component.scss'],
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatDialogActions,
    MatButton,
    MatProgressSpinner,
  ],
})
export class CleanOrphanedOrganisationUsersComponent implements OnInit {
  loading = signal(true);
  processing = signal(false);
  orphanedOrganisationIds = signal<string[]>([]);
  selectedOrganisationId = signal<string | null>(null);
  previewUsers = signal<RootUserPreview[]>([]);
  error = signal<string | null>(null);
  loadFailed = signal(false);
  result = signal<CleanupResult | null>(null);

  private users: RootUserPreview[] = [];

  constructor(
    public dialogRef: MatDialogRef<CleanOrphanedOrganisationUsersComponent>,
    private cleanupService: CleanOrphanedOrganisationUsersService,
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      const directory = await this.cleanupService.loadDirectory();
      const existingOrganisationIds = new Set(directory.organisationIds);
      this.users = directory.users.map((user) => {
        return {
          uid: user.uid,
          email: user.email,
          organisations: normaliseUserOrganisations(user.organisations),
        };
      });
      this.orphanedOrganisationIds.set(
        getOrphanedOrganisationIds(this.users, existingOrganisationIds),
      );
    } catch (error) {
      this.loadFailed.set(true);
      this.error.set(this.getErrorMessage(error));
    } finally {
      this.loading.set(false);
    }
  }

  selectOrganisation(organisationId: string): void {
    this.selectedOrganisationId.set(organisationId);
    this.previewUsers.set(
      this.users.filter((user) =>
        user.organisations.some((organisation) => organisation.id === organisationId),
      ),
    );
    this.error.set(null);
  }

  async cleanUsers(): Promise<void> {
    const organisationId = this.selectedOrganisationId();
    if (!organisationId) {
      return;
    }

    this.processing.set(true);
    this.error.set(null);
    try {
      this.result.set(await this.cleanupService.cleanUsers(organisationId));
    } catch (error) {
      this.error.set(this.getErrorMessage(error));
    } finally {
      this.processing.set(false);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  private getErrorMessage(error: unknown): string {
    console.error('Clean orphaned organisation users failed', error);

    if (error instanceof Error) {
      const code = (error as { code?: string }).code;
      return code ? `${code}: ${error.message}` : error.message;
    }

    return typeof error === 'string' ? error : JSON.stringify(error);
  }
}
